import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import apiClient, { learningAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import logger from '../../utils/logger';

const LessonActivity = () => {
    const { courseId, lessonId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activities, setActivities] = useState([]);
    const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [score, setScore] = useState(0);
    const [activityType, setActivityType] = useState('quiz'); // 'quiz', 'speech', 'written'
    const [practicePlan, setPracticePlan] = useState(null);
    const [planLoading, setPlanLoading] = useState(false);
    const [planError, setPlanError] = useState('');
    
    // Speech practice state
    const [isRecording, setIsRecording] = useState(false);
    const [speechText, setSpeechText] = useState('');
    const [speechFeedback, setSpeechFeedback] = useState(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    
    // Camera state
    const [showCamera, setShowCamera] = useState(false);
    const [stream, setStream] = useState(null);
    const videoRef = useRef(null);

    const lessonData = location.state || {};

    useEffect(() => {
        generateActivities();
        
        return () => {
            // Cleanup camera stream
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [lessonId]);

    const generateActivities = async () => {
        setLoading(true);
        setError('');
        
        logger.log('🎯 Lesson Data:', lessonData);
        
        const requestPayload = {
            lesson_title: lessonData.title || 'English Lesson',
            lesson_description: lessonData.description || 'Practice your English speaking skills',
            video_url: lessonData.videoUrl || '',
            activity_types: ['quiz', 'speech']
        };
        
        logger.log('📤 Activity Generation Request:', requestPayload);
        
        try {
            const response = await apiClient.post('/learning/generate-activity', {
                ...requestPayload,
                activity_types: ['quiz', 'speech']
            });
            
            logger.log('✅ Activity Generation Response:', response.data);
            
            setActivities(response.data.activities || []);
            if (response.data.activities && response.data.activities.length > 0) {
                setActivityType(response.data.activities[0].type);
            }
        } catch (error) {
            logger.error('❌ Failed to generate activities:', error);
            logger.error('Error details:', error.response?.data || error.message);
            
            // Show error state with more details
            setActivities([]);
            const errorMsg = error.response?.data?.detail || error.message || 'Failed to load activities. Please try again later.';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleQuizAnswer = (optionIndex) => {
        setUserAnswers({
            ...userAnswers,
            [currentActivityIndex]: optionIndex
        });
    };

    const handleWrittenAnswer = (text) => {
        setUserAnswers({
            ...userAnswers,
            [currentActivityIndex]: text
        });
    };

    const startSpeechRecording = async () => {
        try {
            const audioStream = await navigator.mediaDevices.getUserMedia({ 
                audio: true,
                video: activities[currentActivityIndex]?.useCamera 
            });
            
            if (activities[currentActivityIndex]?.useCamera && videoRef.current) {
                setShowCamera(true);
                setStream(audioStream);
                videoRef.current.srcObject = audioStream;
            }
            
            const mediaRecorder = new MediaRecorder(audioStream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];
            
            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                await analyzeSpeech(audioBlob);
                
                // Stop camera stream
                if (stream) {
                    stream.getTracks().forEach(track => track.stop());
                    setShowCamera(false);
                }
            };
            
            mediaRecorder.start();
            setIsRecording(true);
        } catch (error) {
            console.error('Error accessing microphone/camera:', error);
            alert('Could not access microphone/camera. Please check permissions.');
        }
    };

    const stopSpeechRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const analyzeSpeech = async (audioBlob) => {
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.wav');
            formData.append('expected_content', activities[currentActivityIndex]?.prompt || '');
            
            const response = await apiClient.post('/learning/speech/analyze', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setSpeechFeedback(response.data);
            setUserAnswers({
                ...userAnswers,
                [currentActivityIndex]: response.data
            });
        } catch (error) {
            console.error('Speech analysis failed:', error);
            setSpeechFeedback({
                error: true,
                message: 'Speech analysis failed. Please try recording again.'
            });
        }
    };

    const submitActivity = () => {
        if (currentActivityIndex < activities.length - 1) {
            setCurrentActivityIndex(currentActivityIndex + 1);
            setActivityType(activities[currentActivityIndex + 1].type);
            setSpeechFeedback(null);
        } else {
            calculateScore();
            setShowResults(true);
        }
    };

    const calculateScore = () => {
        let totalScore = 0;
        activities.forEach((activity, index) => {
            const answer = userAnswers[index];
            if (activity.type === 'quiz') {
                if (answer === activity.correctAnswer) totalScore += activity.points;
            } else if (activity.type === 'speech') {
                if (answer && answer.confidence_score) {
                    totalScore += Math.round((answer.confidence_score / 100) * activity.points);
                }
            }
        });
        setScore(totalScore);
        
        // Calculate max score
        const maxScore = activities.reduce((sum, act) => sum + act.points, 0);
        const scorePercentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
        
        // Record activity completion to backend
        const activityType = activities.length > 0 ? activities[0].type : 'quiz';
        learningAPI.completeActivity(
            activityType,
            scorePercentage,
            courseId ? parseInt(courseId) : null,
            lessonId
        ).then(response => {
            if (response.data.success) {
                logger.log('Activity recorded:', response.data);
                logger.log(`Earned ${response.data.xp_earned} XP and ${response.data.coins_earned} ZenCoins!`);
            }
        }).catch(err => {
            console.error('Failed to record activity:', err);
        });
        
        // Save progress locally as backup
        const totalCoins = localStorage.getItem('totalZenCoins') || 0;
        localStorage.setItem('totalZenCoins', (parseInt(totalCoins) + totalScore).toString());
    };

    const buildActivitySummary = (maxScore) => {
        let quizCorrect = 0;
        let quizTotal = 0;
        let speechConfidence = 0;
        let speechCount = 0;

        activities.forEach((activity, index) => {
            const answer = userAnswers[index];
            if (activity.type === 'quiz') {
                quizTotal += 1;
                if (answer === activity.correctAnswer) quizCorrect += 1;
            } else if (activity.type === 'speech') {
                if (answer && answer.confidence_score) {
                    speechConfidence += answer.confidence_score;
                    speechCount += 1;
                }
            }
        });

        const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
        const avgSpeechConfidence = speechCount > 0 ? Math.round(speechConfidence / speechCount) : 0;

        return {
            score,
            max_score: maxScore,
            percentage,
            quiz_correct: quizCorrect,
            quiz_total: quizTotal,
            avg_speech_confidence: avgSpeechConfidence
        };
    };

    useEffect(() => {
        const fetchPlan = async () => {
            if (!showResults || activities.length === 0) return;
            const maxScore = activities.reduce((sum, act) => sum + act.points, 0);
            const activitySummary = buildActivitySummary(maxScore);

            setPlanLoading(true);
            setPlanError('');
            try {
                const response = await apiClient.post('/learning/personalized-plan', {
                    lesson_title: lessonData.title || 'English Lesson',
                    lesson_description: lessonData.description || '',
                    video_url: lessonData.videoUrl || '',
                    activity_summary: activitySummary
                });
                setPracticePlan(response.data.plan || null);
            } catch (error) {
                console.error('Failed to generate personalized plan:', error);
                setPlanError('Could not generate a personalized plan right now.');
            } finally {
                setPlanLoading(false);
            }
        };

        fetchPlan();
    }, [showResults, activities.length, score]);

    const finishActivities = () => {
        navigate(`/courses/${courseId}/learn`);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <style>{`
                    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                    @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
                    @keyframes gradient { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
                    .ai-icon { animation: float 2s ease-in-out infinite; }
                    .loading-dots span { animation: pulse 1.5s ease-in-out infinite; }
                    .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
                    .loading-dots span:nth-child(3) { animation-delay: 0.4s; }
                `}</style>
                <div style={styles.loadingContainer}>
                    <div className="ai-icon" style={{
                        fontSize: '80px',
                        marginBottom: '30px',
                        filter: 'drop-shadow(0 10px 30px rgba(255,255,255,0.3))'
                    }}>🤖</div>
                    <div style={{
                        width: '70px',
                        height: '70px',
                        border: '4px solid rgba(255,255,255,0.2)',
                        borderTop: '4px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '30px'
                    }}></div>
                    <h2 style={{
                        fontSize: '1.8rem',
                        fontWeight: 'bold',
                        marginBottom: '15px',
                        background: 'linear-gradient(90deg, #fff, #a5b4fc, #fff)',
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        animation: 'gradient 3s ease infinite'
                    }}>Generating AI Assessment</h2>
                    <p style={{
                        fontSize: '1.1rem',
                        opacity: 0.9,
                        marginBottom: '20px'
                    }}>
                        Analyzing video content with Gemini AI
                        <span className="loading-dots">
                            <span>.</span><span>.</span><span>.</span>
                        </span>
                    </p>
                    <div style={{
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '20px',
                        padding: '15px 25px',
                        fontSize: '0.9rem',
                        opacity: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <span style={{fontSize: '1.2rem'}}>✨</span>
                        Creating personalized questions based on lesson: <strong>{lessonData.title || 'English Lesson'}</strong>
                    </div>
                </div>
            </div>
        );
    }

    if (showResults) {
        const maxScore = activities.reduce((sum, act) => sum + act.points, 0);
        const percentage = Math.round((score / maxScore) * 100);
        
        return (
            <div style={styles.container}>
                <div style={styles.resultsCard}>
                    <h1 style={styles.resultsTitle}>🎉 Activity Complete!</h1>
                    <div style={styles.scoreCircle}>
                        <div style={styles.scoreValue}>{percentage}%</div>
                        <div style={styles.scoreLabel}>Score</div>
                    </div>
                    <div style={styles.scoreDetails}>
                        <p>You earned <strong>{score}</strong> out of <strong>{maxScore}</strong> points</p>
                        <p style={styles.coinsEarned}>💰 +{score} ZenCoins added to your account!</p>
                    </div>
                    <div style={styles.resultsActions}>
                        <button onClick={finishActivities} style={styles.primaryBtn}>
                            Continue Learning
                        </button>
                        <button onClick={() => window.location.reload()} style={styles.secondaryBtn}>
                            Retry Activities
                        </button>
                    </div>
                    <div style={styles.planCard}>
                        <h2 style={styles.planTitle}>Your Personalized Practice Plan</h2>
                        {planLoading && <p style={styles.planText}>Generating your plan...</p>}
                        {!planLoading && planError && <p style={styles.planError}>{planError}</p>}
                        {!planLoading && practicePlan && (
                            <div>
                                <p style={styles.planSummary}>{practicePlan.summary}</p>
                                <div style={styles.planSection}>
                                    <div style={styles.planLabel}>Focus areas</div>
                                    <ul style={styles.planList}>
                                        {practicePlan.focus_areas.map((area, idx) => (
                                            <li key={idx}>{area}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div style={styles.planSection}>
                                    <div style={styles.planLabel}>Next steps</div>
                                    <ol style={styles.planList}>
                                        {practicePlan.next_steps.map((step, idx) => (
                                            <li key={idx}>{step}</li>
                                        ))}
                                    </ol>
                                </div>
                                <div style={styles.planMinutes}>
                                    Suggested practice time: {practicePlan.practice_minutes} minutes
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const currentActivity = activities[currentActivityIndex];

    const canProceed = () => {
        if (!currentActivity) return false;
        const type = currentActivity.type;
        if (type === 'speech') return true; // Speech can proceed after recording
        return !!userAnswers[currentActivityIndex]; // Quiz needs answer
    };

    // Guard: no activities loaded (API error or empty response)
    if (!currentActivity) {
        return (
            <div style={styles.container}>
                <div style={{ ...styles.activityCard, textAlign: 'center', padding: '60px 40px' }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>😕</div>
                    <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
                        {error || 'No activities available'}
                    </h2>
                    <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '30px' }}>
                        We couldn't generate activities for this lesson. This may be because the lesson content hasn't been set up yet.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                        <button onClick={() => window.location.reload()} style={styles.primaryBtn}>
                            🔄 Try Again
                        </button>
                        <button onClick={() => navigate(`/courses/${courseId}/learn`)} style={styles.secondaryBtn}>
                            ← Back to Lesson
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>📝 Lesson Activity</h1>
                <div style={styles.progress}>
                    Activity {currentActivityIndex + 1} of {activities.length}
                </div>
                <div style={{display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '12px'}}>
                    {activities.map((act, i) => (
                        <div key={i} style={{
                            width: i === currentActivityIndex ? '30px' : '12px',
                            height: '6px',
                            borderRadius: '3px',
                            background: i < currentActivityIndex ? '#4ade80' : (i === currentActivityIndex ? 'white' : 'rgba(255,255,255,0.3)'),
                            transition: 'all 0.3s'
                        }}></div>
                    ))}
                </div>
            </div>

            <div style={styles.activityCard}>
                {/* Quiz Type */}
                {currentActivity.type === 'quiz' && (
                    <div>
                        <h2 style={styles.questionTitle}>
                            <span style={styles.typeBadge}>Multiple Choice</span>
                        </h2>
                        <p style={styles.question}>{currentActivity.question}</p>
                        <div style={styles.optionsGrid}>
                            {currentActivity.options.map((option, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleQuizAnswer(index)}
                                    style={{
                                        ...styles.optionBtn,
                                        ...(userAnswers[currentActivityIndex] === index ? styles.optionSelected : {})
                                    }}
                                >
                                    <span style={styles.optionLetter}>{String.fromCharCode(65 + index)}</span>
                                    {option}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Speech Practice Type */}
                {currentActivity.type === 'speech' && (
                    <div>
                        <h2 style={styles.questionTitle}>
                            <span style={styles.typeBadge}>🎤 Speech Practice</span>
                        </h2>
                        <p style={styles.question}>{currentActivity.prompt}</p>
                        
                        {showCamera && (
                            <div style={styles.cameraContainer}>
                                <video ref={videoRef} autoPlay muted style={styles.videoPreview}></video>
                            </div>
                        )}
                        
                        <div style={styles.recordingControls}>
                            {!isRecording ? (
                                <button onClick={startSpeechRecording} style={styles.recordBtn}>
                                    <span style={styles.micIcon}>🎙️</span>
                                    Start Recording
                                </button>
                            ) : (
                                <button onClick={stopSpeechRecording} style={styles.stopBtn}>
                                    <span style={styles.stopIcon}>⏹️</span>
                                    Stop Recording
                                </button>
                            )}
                            {isRecording && <div style={styles.recordingIndicator}>● Recording...</div>}
                        </div>
                        
                        {speechFeedback && (
                            <div style={styles.feedbackCard}>
                                <h3 style={styles.feedbackTitle}>📊 Your Performance</h3>
                                <div style={styles.scoresGrid}>
                                    <div style={styles.scoreItem}>
                                        <div style={styles.scoreLabel}>Fluency</div>
                                        <div style={styles.scoreBar}>
                                            <div style={{...styles.scoreBarFill, width: `${speechFeedback.fluency_score}%`}}></div>
                                        </div>
                                        <div style={styles.scoreText}>{speechFeedback.fluency_score}/100</div>
                                    </div>
                                    <div style={styles.scoreItem}>
                                        <div style={styles.scoreLabel}>Pronunciation</div>
                                        <div style={styles.scoreBar}>
                                            <div style={{...styles.scoreBarFill, width: `${speechFeedback.pronunciation_score}%`}}></div>
                                        </div>
                                        <div style={styles.scoreText}>{speechFeedback.pronunciation_score}/100</div>
                                    </div>
                                    <div style={styles.scoreItem}>
                                        <div style={styles.scoreLabel}>Confidence</div>
                                        <div style={styles.scoreBar}>
                                            <div style={{...styles.scoreBarFill, width: `${speechFeedback.confidence_score}%`}}></div>
                                        </div>
                                        <div style={styles.scoreText}>{speechFeedback.confidence_score}/100</div>
                                    </div>
                                </div>
                                {speechFeedback.transcription && (
                                    <div style={styles.transcription}>
                                        <strong>Transcription:</strong> {speechFeedback.transcription}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div style={styles.activityFooter}>
                    <div style={styles.points}>
                        💎 {currentActivity.points} points
                    </div>
                    <button 
                        onClick={submitActivity}
                        disabled={!canProceed()}
                        style={{
                            ...styles.submitBtn,
                            ...(!canProceed() ? styles.submitBtnDisabled : {})
                        }}
                    >
                        {currentActivityIndex < activities.length - 1 ? 'Next Activity →' : 'Finish & View Results'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '40px 20px'
    },
    header: {
        textAlign: 'center',
        marginBottom: '30px',
        color: 'white'
    },
    title: {
        fontSize: '2.5rem',
        marginBottom: '10px',
        textShadow: '0 2px 10px rgba(0,0,0,0.2)'
    },
    progress: {
        fontSize: '1.1rem',
        opacity: 0.9
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
        color: 'white',
        textAlign: 'center',
        padding: '20px'
    },
    activityCard: {
        maxWidth: '900px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
    },
    questionTitle: {
        fontSize: '1.5rem',
        color: '#333',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
    },
    typeBadge: {
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        color: 'white',
        padding: '8px 20px',
        borderRadius: '20px',
        fontSize: '0.9rem',
        fontWeight: 'bold'
    },
    question: {
        fontSize: '1.2rem',
        color: '#555',
        marginBottom: '30px',
        lineHeight: '1.6'
    },
    optionsGrid: {
        display: 'grid',
        gap: '15px',
        marginBottom: '30px'
    },
    optionBtn: {
        padding: '20px',
        border: '2px solid #e0e0e0',
        borderRadius: '12px',
        background: 'white',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: '1rem',
        transition: 'all 0.3s',
        display: 'flex',
        alignItems: 'center',
        gap: '15px'
    },
    optionSelected: {
        border: '2px solid #667eea',
        background: '#f0f4ff',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
    },
    optionLetter: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        background: '#667eea',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        flexShrink: 0
    },
    recordingControls: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '15px',
        margin: '30px 0'
    },
    recordBtn: {
        padding: '20px 40px',
        background: 'linear-gradient(135deg, #27b05b, #22a04e)',
        color: 'white',
        border: 'none',
        borderRadius: '30px',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 4px 15px rgba(39, 176, 91, 0.3)',
        transition: 'all 0.3s'
    },
    stopBtn: {
        padding: '20px 40px',
        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
        color: 'white',
        border: 'none',
        borderRadius: '30px',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
        transition: 'all 0.3s'
    },
    micIcon: {
        fontSize: '1.5rem'
    },
    stopIcon: {
        fontSize: '1.3rem'
    },
    recordingIndicator: {
        color: '#dc2626',
        fontSize: '1rem',
        fontWeight: 'bold',
        animation: 'pulse 1.5s ease-in-out infinite'
    },
    cameraContainer: {
        marginBottom: '20px',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    videoPreview: {
        width: '100%',
        maxWidth: '500px',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
    },
    feedbackCard: {
        background: '#f8f9fa',
        padding: '25px',
        borderRadius: '12px',
        marginTop: '20px'
    },
    feedbackTitle: {
        fontSize: '1.2rem',
        marginBottom: '20px',
        color: '#333'
    },
    scoresGrid: {
        display: 'grid',
        gap: '15px',
        marginBottom: '15px'
    },
    scoreItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    scoreLabel: {
        fontSize: '0.9rem',
        color: '#666',
        fontWeight: 'bold'
    },
    scoreBar: {
        height: '10px',
        background: '#e0e0e0',
        borderRadius: '5px',
        overflow: 'hidden'
    },
    scoreBarFill: {
        height: '100%',
        background: 'linear-gradient(90deg, #27b05b, #4ade80)',
        borderRadius: '5px',
        transition: 'width 0.5s ease'
    },
    scoreText: {
        fontSize: '0.85rem',
        color: '#666'
    },
    transcription: {
        marginTop: '15px',
        padding: '15px',
        background: 'white',
        borderRadius: '8px',
        fontSize: '0.95rem',
        color: '#555'
    },
    textarea: {
        width: '100%',
        padding: '15px',
        border: '2px solid #e0e0e0',
        borderRadius: '12px',
        fontSize: '1rem',
        fontFamily: 'inherit',
        resize: 'vertical',
        marginBottom: '10px'
    },
    wordCount: {
        textAlign: 'right',
        color: '#888',
        fontSize: '0.9rem',
        marginBottom: '20px'
    },
    activityFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '30px',
        paddingTop: '30px',
        borderTop: '2px solid #f0f0f0'
    },
    points: {
        fontSize: '1.1rem',
        color: '#667eea',
        fontWeight: 'bold'
    },
    submitBtn: {
        padding: '15px 35px',
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        color: 'white',
        border: 'none',
        borderRadius: '25px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
        transition: 'all 0.3s'
    },
    submitBtnDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed'
    },
    resultsCard: {
        maxWidth: '600px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '20px',
        padding: '50px',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
    },
    resultsTitle: {
        fontSize: '2.5rem',
        color: '#333',
        marginBottom: '30px'
    },
    scoreCircle: {
        width: '180px',
        height: '180px',
        margin: '30px auto',
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        borderRadius: '50%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
    },
    scoreValue: {
        fontSize: '3rem',
        fontWeight: 'bold'
    },
    scoreLabel: {
        fontSize: '1rem',
        opacity: 0.9
    },
    scoreDetails: {
        fontSize: '1.1rem',
        color: '#555',
        marginBottom: '40px'
    },
    coinsEarned: {
        marginTop: '15px',
        fontSize: '1.3rem',
        color: '#27b05b',
        fontWeight: 'bold'
    },
    resultsActions: {
        display: 'flex',
        gap: '15px',
        justifyContent: 'center'
    },
    planCard: {
        marginTop: '30px',
        padding: '24px',
        borderRadius: '16px',
        background: '#f7f9ff',
        border: '1px solid #e3e8ff',
        textAlign: 'left'
    },
    planTitle: {
        fontSize: '1.4rem',
        marginBottom: '10px',
        color: '#2d2d2d'
    },
    planText: {
        color: '#555',
        marginBottom: '10px'
    },
    planError: {
        color: '#b00020',
        marginBottom: '10px'
    },
    planSummary: {
        color: '#444',
        marginBottom: '16px'
    },
    planSection: {
        marginBottom: '12px'
    },
    planLabel: {
        fontWeight: 'bold',
        marginBottom: '6px'
    },
    planList: {
        paddingLeft: '18px',
        margin: 0,
        color: '#444'
    },
    planMinutes: {
        marginTop: '10px',
        color: '#555'
    },
    primaryBtn: {
        padding: '15px 35px',
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        color: 'white',
        border: 'none',
        borderRadius: '25px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
    },
    secondaryBtn: {
        padding: '15px 35px',
        background: 'white',
        color: '#667eea',
        border: '2px solid #667eea',
        borderRadius: '25px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer'
    }
};

export default LessonActivity;
